import cn from "classnames";
import { Datetime, Field, Select } from "components/shared/form";
import {
    documentsTrackingStatuses,
    documentsTrackingStatusesEnum,
    parcelProviderTypesEnum,
    parcelRelationTypesEnum,
} from "helpers/enums";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { applyBidPaymentDocumentsTracking } from "sources/books/bids/closeBids";
import { applyParcel } from "sources/parcel";

const trackingModes = [
    { label: "Почта России", value: parcelProviderTypesEnum.RussianPost },
    { label: "СДЭК", value: parcelProviderTypesEnum.Cdek },
    { label: "Ручное отслеживание", value: "manual" },
];

const dateFields = ["plannedArrivalDate", "documentsReceiptDate"];

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

export default function TrackingModal({ bid, getList, params, back }) {
    const payment = bid.payment ?? {};
    const [mode, setMode] = useState(parcelProviderTypesEnum.RussianPost);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        externalId: "",
        documentsTrackingStatus: payment.documentsTrackingStatus || documentsTrackingStatusesEnum.NotShipped,
        plannedArrivalDate: formatDate(payment.plannedArrivalDate),
        documentsReceiptDate: formatDate(payment.documentsReceiptDate),
    });

    const isManual = mode === "manual";
    const isInTransit = data.documentsTrackingStatus === documentsTrackingStatusesEnum.InTransit;
    const isDelivered = data.documentsTrackingStatus === documentsTrackingStatusesEnum.Delivered;
    const hasPlannedArrivalDate = isInTransit || isDelivered;
    const canSave = useMemo(() => {
        if (loading) {
            return false;
        }

        return isManual
            ? !!data.documentsTrackingStatus && (!isDelivered || !!data.documentsReceiptDate)
            : !!data.externalId?.trim();
    }, [data, isDelivered, isManual, loading]);

    const handleChange = useCallback(({ name, value }) => {
        setData((prev) => ({
            ...prev,
            [name]: dateFields.includes(name) ? formatDate(value) : value,
            ...(name === "documentsTrackingStatus" && value === documentsTrackingStatusesEnum.NotShipped
                ? { plannedArrivalDate: "", documentsReceiptDate: "" }
                : {}),
            ...(name === "documentsTrackingStatus" && value !== documentsTrackingStatusesEnum.Delivered
                ? { documentsReceiptDate: "" }
                : {}),
        }));
    }, []);

    const refreshList = useCallback(() => getList?.(params), [getList, params]);

    const handleSave = useCallback(() => {
        if (!canSave) {
            return;
        }

        setLoading(true);
        const request = isManual
            ? applyBidPaymentDocumentsTracking({
                  bidId: bid.id,
                  documentsTrackingStatus: data.documentsTrackingStatus,
                  plannedArrivalDate: data.plannedArrivalDate || null,
                  documentsReceiptDate: data.documentsReceiptDate || null,
              })
            : applyParcel({
                  entityId: bid.id,
                  relationType: parcelRelationTypesEnum.Bid,
                  provider: mode,
                  externalId: data.externalId.trim(),
              });

        request.then((response) => {
            if (response) {
                refreshList();
                back();
            }
            setLoading(false);
        });
    }, [back, bid.id, canSave, data, isManual, mode, refreshList]);

    return (
        <div className={cn("modal-window__body mb-0", { "loading loading--small": loading })}>
            <div className="btn-group btn-group-sm d-flex mb-3" role="group">
                {trackingModes.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        className={cn("btn", mode === item.value ? "btn-brand" : "btn-secondary")}
                        onClick={() => setMode(item.value)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {isManual ? (
                <div className="text-left">
                    <Select
                        label="Статус"
                        inputProps={{ name: "documentsTrackingStatus" }}
                        options={documentsTrackingStatuses}
                        value={data.documentsTrackingStatus}
                        onChange={handleChange}
                        menuPortalTarget={document.body}
                        unClearable
                    />
                    {hasPlannedArrivalDate && (
                        <Datetime
                            label="Плановая дата прибытия"
                            inputProps={{ name: "plannedArrivalDate", type: "date" }}
                            value={data.plannedArrivalDate}
                            onChange={handleChange}
                        />
                    )}
                    {isDelivered && (
                        <Datetime
                            label="Дата получения"
                            inputProps={{ name: "documentsReceiptDate", type: "date" }}
                            value={data.documentsReceiptDate}
                            onChange={handleChange}
                            required
                        />
                    )}
                </div>
            ) : (
                <Field
                    label="Трек-номер"
                    inputProps={{
                        name: "externalId",
                        placeholder: "Введите трек-номер",
                    }}
                    value={data.externalId}
                    onChange={handleChange}
                />
            )}

            <div className="modal-window__footer modal-window__footer--right px-0 pb-0">
                <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={back}>
                    Отменить
                </button>
                <button type="button" className="btn btn-brand btn-sm" onClick={handleSave} disabled={!canSave}>
                    Сохранить
                </button>
            </div>
        </div>
    );
}
